<script setup lang="ts">
import { Bell, Activity, LayoutDashboard, Moon, Palette, Settings2, Sun } from '@lucide/vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useAppearanceStore } from '@/stores/appearance'

defineProps<{ backTo?: string; backLabel?: string }>()

const router = useRouter()
const auth = useAuthStore()
const appearance = useAppearanceStore()

function logout(): void {
  void router.push({ name: 'logout' })
}

function toggleTheme(): void {
  appearance.setTheme(appearance.isDark ? 'light' : 'dark')
  void appearance.save(auth.token, appearance.settings).catch(() => undefined)
}
</script>

<template>
  <div class="min-h-screen">
    <header
      class="sticky top-0 z-30 border-b border-slate-200/80 bg-canvas/90 backdrop-blur-xl dark:border-slate-700/80 dark:bg-slate-950/90"
    >
      <div
        class="mx-auto flex min-h-16 max-w-[1500px] items-center justify-between gap-3 px-4 py-2 sm:px-6 lg:px-8"
      >
        <div class="flex min-w-0 items-center gap-3">
          <RouterLink
            to="/"
            class="group flex shrink-0 items-center gap-2.5 rounded-md"
            :aria-label="$t('nav.dashboardAria')"
          >
            <span
              class="grid size-9 place-items-center rounded-md bg-ink text-white shadow-sm transition group-hover:bg-moss-700 dark:bg-moss-700"
            >
              <svg viewBox="0 0 24 24" class="size-5" fill="none" aria-hidden="true">
                <path
                  d="M3 12h3.2l2-5.2 3.2 10.5 2.4-7.2 1.6 3.9H21"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
            <span class="hidden font-display text-lg font-bold tracking-tight sm:block">{{
              $t('brand')
            }}</span>
          </RouterLink>
          <template v-if="backTo">
            <span class="text-slate-300 dark:text-slate-700">/</span>
            <RouterLink
              :to="backTo"
              class="truncate text-sm font-medium text-slate-500 transition hover:text-moss-700 dark:hover:text-moss-500"
            >
              {{ backLabel || $t('common.back') }}
            </RouterLink>
          </template>
        </div>

        <nav class="flex items-center gap-1" :aria-label="$t('nav.settings')">
          <RouterLink to="/" class="nav-button" :title="$t('nav.dashboard')">
            <LayoutDashboard class="size-4" aria-hidden="true" />
            <span class="hidden xl:inline">{{ $t('nav.dashboard') }}</span>
          </RouterLink>
          <RouterLink to="/uptime" class="nav-button" :title="$t('nav.uptime')">
            <Activity class="size-4" aria-hidden="true" />
            <span class="hidden xl:inline">{{ $t('nav.uptime') }}</span>
          </RouterLink>
          <RouterLink to="/settings/alerts" class="nav-button" :title="$t('nav.alerts')">
            <Bell class="size-4" aria-hidden="true" />
            <span class="hidden xl:inline">{{ $t('nav.alerts') }}</span>
          </RouterLink>
          <RouterLink to="/settings/appearance" class="nav-button" :title="$t('nav.appearance')">
            <Palette class="size-4" aria-hidden="true" />
            <span class="hidden xl:inline">{{ $t('nav.appearance') }}</span>
          </RouterLink>
          <RouterLink to="/settings/admin" class="nav-button" :title="$t('nav.admin')">
            <Settings2 class="size-4" aria-hidden="true" />
            <span class="hidden xl:inline">{{ $t('nav.admin') }}</span>
          </RouterLink>
          <button
            type="button"
            class="nav-button"
            :title="$t(appearance.isDark ? 'nav.lightMode' : 'nav.darkMode')"
            @click="toggleTheme"
          >
            <Sun v-if="appearance.isDark" class="size-4" aria-hidden="true" />
            <Moon v-else class="size-4" aria-hidden="true" />
            <span class="sr-only">{{
              $t(appearance.isDark ? 'nav.lightMode' : 'nav.darkMode')
            }}</span>
          </button>
          <span class="mx-1 hidden h-5 w-px bg-slate-200 lg:block dark:bg-slate-700"></span>
          <span class="hidden items-center gap-2 text-xs text-slate-500 2xl:flex">
            <span class="size-1.5 rounded-full bg-moss-500"></span>{{ $t('nav.monitorOnly') }}
          </span>
          <button class="btn-quiet !min-h-9 !px-3" type="button" @click="logout">
            {{ $t('nav.signOut') }}
          </button>
        </nav>
      </div>
    </header>
    <main class="mx-auto max-w-[1500px] px-4 py-7 sm:px-6 sm:py-10 lg:px-8"><slot /></main>
    <footer class="mx-auto max-w-[1500px] px-6 pb-5 text-right text-[10px] text-slate-400">
      <a href="https://db-ip.com" target="_blank" rel="noopener noreferrer"
        >IP Geolocation by DB-IP</a
      >
    </footer>
  </div>
</template>
